import React, { useState } from 'react';
import { Download, Zap } from 'lucide-react';
import jsPDF from 'jspdf';
const AHUCalculator = () => {
  // AHU Calculator State
  const [ahuData, setAhuData] = useState({
    clientName: '',
    coilWMm: '',
    coilHMm: '',
    uvLampLMm: '',
    distance: '',
    airflowVolCfm: '',
    numRows: '',
    numCols: '',
    lampCount: '',
    totalWattage: '',
    rowWattage: '',
    intensityAt1m: '',
    uvWattageW: '',
    noOfLamps: '',
    distBtwnLamp: ''
  });

  const [ahuCalculations, setAhuCalculations] = useState({
    uvLampLInch: 0,
    uvLampLCm: 0,
    t2LA: 0,
    eWcm2: 0,
    eMWcm2: 0,
    exposureTimeTs: 0,
    doseEt: 0,
    totalIntensityMWcm2: 0,
    doseJm2: 0,
    noOfLamps: 0,
    correctedUVD: 0,
    correctedUVDx001: 0,
    minimumInt: 0,
    maximumInt: 0,
    avgInt: 0
  });

  // Helper functions for conversions
  const mmToCm = (mm) => parseFloat(mm) / 10;
  const cmToM = (cm) => parseFloat(cm) * 0.01;
  const mmToInch = (mm) => parseFloat(mm) / 25.4;

  // AHU Calculator Functions
  const handleAhuCalculation = () => {
    const airflowVolCfm = parseFloat(ahuData.airflowVolCfm);
    const coilWMm = parseFloat(ahuData.coilWMm);
    const coilHMm = parseFloat(ahuData.coilHMm);
    const uvLampLMm = parseFloat(ahuData.uvLampLMm);
    const uvWattageW = parseFloat(ahuData.uvWattageW);
    const distance = parseFloat(ahuData.distance);
    const numRows = parseFloat(ahuData.numRows);
    const totalWattage = parseFloat(ahuData.totalWattage);
    const rowWattage = parseFloat(ahuData.rowWattage);
    const noOfLamps = parseFloat(ahuData.noOfLamps);
    const distBtwnLamp = parseFloat(ahuData.distBtwnLamp);

    // Basic conversions
    const uvLampLInch = uvLampLMm / 25.4;
    const uvLampLCm = uvLampLMm / 10;
    const t2LA = 6.28 * uvLampLCm * distance;
    const eWcm2 = uvWattageW / t2LA;
    const eMWcm2 = eWcm2 * 1000000;

    // Exposure time calculation
    const distanceInM = distance * 0.01;
    const coilAreaMm2 = coilWMm * coilHMm;
    const coilAreaM2 = coilAreaMm2 / 1000000;
    const valFlowRateM3S = airflowVolCfm * 0.000471947;
    const flowVelMs = valFlowRateM3S / coilAreaM2;
    const exposureTimeTs = distanceInM / flowVelMs;

    const doseEt = eMWcm2 * exposureTimeTs;
    const totalIntensityMWcm2 = noOfLamps * eMWcm2;
    const doseJm2 = doseEt * 0.01;
    const noOfLampsCalc = coilHMm / distBtwnLamp;

    // Complex calculations for final results
    const lengthInCm = coilHMm / 10;
    const widthInCm = coilWMm / 10;
    const uvLampLMm1 = uvLampLMm / 10;

    const totalAverageIntensity = ((totalWattage * 0.3) * (Math.log(distance / 2) + 0.577) * (10 ** 6)) / (2 * Math.PI * widthInCm * (distance / 2));
    const contactTime = (((distance / 30.48) * 1.5 * (lengthInCm / 30.48) * (widthInCm / 30.48)) / airflowVolCfm) * 60;
    const correctedDosage = totalAverageIntensity * contactTime * 0.7;
    const correctedUVDx001 = correctedDosage * 0.01;

    const diagonalDistance = Math.sqrt((lengthInCm / (numRows * 2)) ** 2 + distance ** 2);
    const minIntensity = ((rowWattage * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * diagonalDistance));
    let maxIntensity = ((rowWattage * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * distance));

    if (numRows >= 3) {
      maxIntensity += minIntensity * 2;
    }

    const avgIntensity = ((minIntensity * 2) + (numRows * maxIntensity) + (numRows - 1) * (minIntensity * 2)) / (numRows * 2 + 1);

    setAhuCalculations({
      uvLampLInch: parseFloat(uvLampLInch.toFixed(2)),
      uvLampLCm: parseFloat(uvLampLCm.toFixed(2)),
      t2LA: parseFloat(t2LA.toFixed(2)),
      eWcm2: parseFloat(eWcm2.toFixed(4)),
      eMWcm2: parseFloat(eMWcm2.toFixed(2)),
      exposureTimeTs: parseFloat(exposureTimeTs.toFixed(4)),
      doseEt: parseFloat(doseEt.toFixed(2)),
      totalIntensityMWcm2: parseFloat(totalIntensityMWcm2.toFixed(2)),
      doseJm2: parseFloat(doseJm2.toFixed(3)),
      noOfLamps: parseFloat(noOfLampsCalc.toFixed(2)),
      correctedUVD: parseInt(correctedDosage.toFixed(0)),
      correctedUVDx001: parseFloat(correctedUVDx001.toFixed(2)),
      minimumInt: parseInt(minIntensity.toFixed(0)),
      maximumInt: parseInt(maxIntensity.toFixed(0)),
      avgInt: parseInt(avgIntensity.toFixed(0))
    });
  };

  // PDF Generation Function using jsPDF
  const generatePDF = (includeCalculations = true) => {
    // Create a new jsPDF instance
    // const { jsPDF } = window.jspdf;
    const doc = new jsPDF();

    let yPos = 20;
    const leftMargin = 20;
    const rightMargin = 105;
    const lineHeight = 8;

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("AHU-Result", leftMargin, yPos);

    // Logo placeholder (you can add actual logo)
    doc.setFontSize(10);
    doc.text("LOGO", 170, yPos);

    yPos += 20;

    // Client Information Section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Client Information:", leftMargin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const clientInfo = [
      `Client Name: ${ahuData.clientName || 'N/A'}`,
      `Coil Width: ${ahuData.coilWMm || 'N/A'} mm`,
      `Coil Height: ${ahuData.coilHMm || 'N/A'} mm`,
      `Lamp Length: ${ahuData.uvLampLMm || 'N/A'} mm`,
      `Distance: ${ahuData.distance || 'N/A'} cm`,
      `Flow Rate: ${ahuData.airflowVolCfm || 'N/A'} cfm`,
      `Number of Rows: ${ahuData.numRows || 'N/A'} Nos`,
      `Number of Columns: ${ahuData.numCols || 'N/A'} Nos`,
      `Total Count: ${ahuData.lampCount || 'N/A'} Nos`,
      `Total Lamp Wattage: ${ahuData.totalWattage || 'N/A'} Watts`,
      `Row Lamp Wattage: ${ahuData.rowWattage || 'N/A'} Watts`
    ];

    clientInfo.forEach(info => {
      doc.text(info, leftMargin, yPos);
      yPos += lineHeight;
    });

    yPos += 10;

    // Detailed Calculations (if included)
    if (includeCalculations) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Detailed Calculations:", rightMargin, 50);

      let calcYPos = 60;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const calculations = [
        `UV Lamp (L) (cm): ${ahuCalculations.uvLampLCm}`,
        `2ΠLA: ${ahuCalculations.t2LA}`,
        `E= Φ/2ΠLA (W/cm²): ${ahuCalculations.eWcm2}`,
        `E (µW/cm²): ${ahuCalculations.eMWcm2}`,
        `Exposure time t (s): ${ahuCalculations.exposureTimeTs}`,
        `Dose= E*t (µW.s/cm²): ${ahuCalculations.doseEt}`,
        `Total intensity (µW/cm²): ${ahuCalculations.totalIntensityMWcm2}`
      ];

      calculations.forEach(calc => {
        doc.text(calc, rightMargin, calcYPos);
        calcYPos += lineHeight;
      });
    }

    // Simulation Results
    yPos += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Simulation Result:", leftMargin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const results = [
      `Your Corrected UV Dosage is ${ahuCalculations.correctedUVD} microwatt/sq.cm./sec.`,
      `Your Corrected UV Dosage in J/m² is ${ahuCalculations.correctedUVDx001}.`,
      `Your Minimum Intensity is ${ahuCalculations.minimumInt} microwatt/sq.cm.`,
      `Your Maximum Intensity is ${ahuCalculations.maximumInt} microwatt/sq.cm.`,
      `Your Average Intensity is ${ahuCalculations.avgInt} microwatt/sq.cm.`
    ];

    results.forEach(result => {
      const splitText = doc.splitTextToSize(result, 170);
      doc.text(splitText, leftMargin, yPos);
      yPos += lineHeight * splitText.length;
    });

    // Footer - Company Information
    yPos = 250; // Position footer at bottom
    doc.setDrawColor(0);
    doc.line(leftMargin, yPos, 190, yPos); // Horizontal line
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 255); // Blue color
    doc.text("ULTRAPURE ENVIROCARE PRIVATE LIMITED", leftMargin, yPos);
    yPos += 6;
    doc.text("(FORMERLY ULTRAFRESH MARKETING PVT LTD)", leftMargin, yPos);
    yPos += 10;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0); // Black color
    doc.text("CIN: U51909DL2003PTC120170", leftMargin, yPos);
    yPos += 8;

    const footerText = "Registered Office: CB-344, 2nd Floor, Ring road, Naraina, New Delhi – 110028, Email: info@ultrapureindia.in | Website: www.ultrapureindia.in | Toll Free No.: 1800 11 00 50. Telephone: +91 7428534141.";
    const splitFooter = doc.splitTextToSize(footerText, 170);
    doc.text(splitFooter, leftMargin, yPos);
    yPos += 6 * splitFooter.length;

    doc.text("Branch Locations: Delhi | Kolkata | Nagpur | Ahmedabad | Bangluru | Chennai | Pune | Hyderabad.", leftMargin, yPos);

    // Save the PDF
    const fileName = `AHU-Calculations${includeCalculations ? '' : '-Simple'}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center">
            <Zap className="mr-3 text-blue-600" size={32} />
            AHU Calculator
          </h1>
        </div>

        {/* AHU Calculator */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-blue-600 mb-4">User Input</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.clientName}
                  onChange={(e) => setAhuData({ ...ahuData, clientName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coil (W) [mm]</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.coilWMm}
                  onChange={(e) => setAhuData({ ...ahuData, coilWMm: e.target.value })}
                />
                {ahuData.coilWMm && (
                  <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.coilWMm).toFixed(1)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coil (H) [mm]</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.coilHMm}
                  onChange={(e) => setAhuData({ ...ahuData, coilHMm: e.target.value })}
                />
                {ahuData.coilHMm && (
                  <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.coilHMm).toFixed(1)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) [mm]</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.uvLampLMm}
                  onChange={(e) => setAhuData({ ...ahuData, uvLampLMm: e.target.value })}
                />
                {ahuData.uvLampLMm && (
                  <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.uvLampLMm).toFixed(1)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lamp Coil Distance (cm)</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.distance}
                  onChange={(e) => setAhuData({ ...ahuData, distance: e.target.value })}
                />
                {ahuData.distance && (
                  <p className="text-sm text-gray-500 mt-1">in m: {cmToM(ahuData.distance).toFixed(2)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Airflow Vol. (CFM)</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.airflowVolCfm}
                  onChange={(e) => setAhuData({ ...ahuData, airflowVolCfm: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                  <input
                    type="number"
                    placeholder="of lamps"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={ahuData.numRows}
                    onChange={(e) => setAhuData({ ...ahuData, numRows: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                  <input
                    type="number"
                    placeholder="of lamps"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={ahuData.numCols}
                    onChange={(e) => setAhuData({ ...ahuData, numCols: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Count</label>
                <input
                  type="number"
                  placeholder="of lamps"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.lampCount}
                  onChange={(e) => setAhuData({ ...ahuData, lampCount: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Lamp Wattage (W)</label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={ahuData.totalWattage}
                    onChange={(e) => setAhuData({ ...ahuData, totalWattage: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Row Lamp Wattage (W)</label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={ahuData.rowWattage}
                    onChange={(e) => setAhuData({ ...ahuData, rowWattage: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intensity at 10 inch (µW/cm²)</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.intensityAt1m}
                  onChange={(e) => setAhuData({ ...ahuData, intensityAt1m: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UV wattage (W), Φ</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.uvWattageW}
                  onChange={(e) => setAhuData({ ...ahuData, uvWattageW: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. of lamps</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.noOfLamps}
                  onChange={(e) => setAhuData({ ...ahuData, noOfLamps: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Distance between Lamp (mm)</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={ahuData.distBtwnLamp}
                  onChange={(e) => setAhuData({ ...ahuData, distBtwnLamp: e.target.value })}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setAhuData({
                      clientName: '',
                      coilWMm: '',
                      coilHMm: '',
                      uvLampLMm: '',
                      distance: '',
                      airflowVolCfm: '',
                      numRows: '',
                      numCols: '',
                      lampCount: '',
                      totalWattage: '',
                      rowWattage: '',
                      intensityAt1m: '',
                      uvWattageW: '',
                      noOfLamps: '',
                      distBtwnLamp: ''
                    });
                    setAhuCalculations({
                      uvLampLInch: 0,
                      uvLampLCm: 0,
                      t2LA: 0,
                      eWcm2: 0,
                      eMWcm2: 0,
                      exposureTimeTs: 0,
                      doseEt: 0,
                      totalIntensityMWcm2: 0,
                      doseJm2: 0,
                      noOfLamps: 0,
                      correctedUVD: 0,
                      correctedUVDx001: 0,
                      minimumInt: 0,
                      maximumInt: 0,
                      avgInt: 0
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleAhuCalculation}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Calculate
                </button>
              </div>
            </div>
          </div>

          {/* Calculations 1 */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">1</span></h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) (inch)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.uvLampLInch}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UV Lamp (L) (cm)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.uvLampLCm}
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">2ΠLA</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.t2LA}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E= Φ/2ΠLA (W/cm²)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.eWcm2}
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E (µW/cm²)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.eMWcm2}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exposure time t (s)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.exposureTimeTs}
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dose= E*t (µW.s/cm²)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.doseEt}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total intensity (µW/cm²)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.totalIntensityMWcm2}
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dose (J/m²)</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.doseJm2}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No.of Lamps</label>
                  <input
                    type="text"
                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                    value={ahuCalculations.noOfLamps}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Calculations 2 */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">2</span></h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Corrected UV Dosage <span className="text-gray-500">(microwatt/sq.cm/sec)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                  value={ahuCalculations.correctedUVD}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Corrected UV Dosage X 0.01 <span className="text-gray-500">(J / Sq. meters)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                  value={ahuCalculations.correctedUVDx001}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                  value={ahuCalculations.minimumInt}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                  value={ahuCalculations.maximumInt}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                </label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                  value={ahuCalculations.avgInt}
                  readOnly
                />
              </div>
            </div>

            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Download className="mr-2" size={16} />
                PDF Download
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="all-ahu"
                    name="ahu-pdf"
                    className="text-blue-600"
                    onChange={() => generatePDF(true)}
                  />
                  <label htmlFor="all-ahu" className="text-sm text-gray-700 cursor-pointer">
                    All (with detailed calculations)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="simple-ahu"
                    name="ahu-pdf"
                    className="text-blue-600"
                    onChange={() => generatePDF(false)}
                  />
                  <label htmlFor="simple-ahu" className="text-sm text-gray-700 cursor-pointer">
                    Without Calculation 1
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AHUCalculator;